import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Folder, FileText, Image as ImageIcon, Film, Music, FileJson, File as FileIcon,
    Copy, Eye, ChevronRight, ChevronLeft, Search, X, BarChart3, Loader2, HardDrive,
    FileStack, RefreshCw, ArrowRightLeft, XSquare, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, fmtBytes, fmtDate, fileKind, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { EXPLORER } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogFooter, DialogTitle, DialogDescription, DialogHeader,
} from '@/components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

function fileIcon(name) {
    const k = fileKind(name);
    const cls = 'shrink-0';
    if (k === 'image') return <ImageIcon size={14} className={cls} />;
    if (k === 'video') return <Film size={14} className={cls} />;
    if (k === 'audio') return <Music size={14} className={cls} />;
    if (k === 'pdf') return <FileText size={14} className={cls} />;
    if (k === 'text') return <FileJson size={14} className={cls} />;
    return <FileIcon size={14} className={cls} />;
}

function Breadcrumb({ prefix, onNav }) {
    const parts = prefix.split('/').filter(Boolean);
    return (
        <nav
            data-testid={EXPLORER.breadcrumb}
            className="flex items-center gap-1 flex-wrap text-sm text-muted-foreground"
        >
            <button
                onClick={() => onNav('')}
                className="hover:text-primary row-hover"
            >
                / root
            </button>
            {parts.map((p, i) => {
                const next = parts.slice(0, i + 1).join('/') + '/';
                return (
                    <span key={i} className="flex items-center gap-1">
                        <ChevronRight size={11} className="text-border" />
                        <button
                            onClick={() => onNav(next)}
                            className="hover:text-primary row-hover"
                        >
                            {p}
                        </button>
                    </span>
                );
            })}
        </nav>
    );
}

function PreviewModal({ space, fileKey, onClose, cdnUrl }) {
    const [url, setUrl] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [textContent, setTextContent] = useState(null);
    const kind = fileKey ? fileKind(fileKey) : 'other';
    const name = fileKey ? fileKey.split('/').pop() : '';

    useEffect(() => {
        if (!fileKey) return;
        (async () => {
            setLoading(true);
            setUrl(null); setMeta(null); setTextContent(null);
            try {
                const [pv, oi] = await Promise.all([
                    api.get(`/spaces/${space}/preview`, { params: { key: fileKey } }),
                    api.get(`/spaces/${space}/object`, { params: { key: fileKey } }),
                ]);
                setUrl(pv.data.url);
                setMeta(oi.data);
                if (kind === 'text' && pv.data.url) {
                    try {
                        const res = await fetch(pv.data.url);
                        const txt = await res.text();
                        setTextContent(txt.slice(0, 200000));
                    } catch { /* ignore */ }
                }
            } catch (e) {
                toast.error(formatApiError(e));
            } finally { setLoading(false); }
        })();
    }, [fileKey, space, kind]);

    const publicUrl = cdnUrl && fileKey ? `${cdnUrl.replace(/\/$/, '')}/${fileKey}` : null;

    const copy = async (text, label = 'URL') => {
        try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
        catch { toast.error('Failed to copy'); }
    };

    return (
        <Dialog open={!!fileKey} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                data-testid={EXPLORER.previewDialog}
                className="bg-popover border-border rounded-sm max-w-4xl p-0 overflow-hidden"
            >
                <DialogHeader className="p-4 border-b border-border">
                    <DialogTitle className="font-display flex items-center gap-2 truncate">
                        {fileIcon(name)}
                        <span className="truncate">{name}</span>
                    </DialogTitle>
                    <DialogDescription className="font-mono text-xs text-muted-foreground truncate">
                        {fileKey}
                    </DialogDescription>
                </DialogHeader>
                <div className="p-4 max-h-[65vh] overflow-y-auto space-y-4">
                    {loading ? (
                        <div className="grid place-items-center h-52">
                            <Loader2 className="animate-spin text-primary" size={24} />
                        </div>
                    ) : (
                        <>
                            {kind === 'image' && url && (
                                <img src={url} alt={name} className="max-w-full mx-auto rounded-sm border border-border" />
                            )}
                            {kind === 'video' && url && (
                                <video src={url} controls className="w-full rounded-sm border border-border" />
                            )}
                            {kind === 'audio' && url && (
                                <audio src={url} controls className="w-full" />
                            )}
                            {kind === 'pdf' && url && (
                                <iframe src={url} title={name} className="w-full h-[60vh] border border-border rounded-sm bg-white" />
                            )}
                            {kind === 'text' && (
                                <pre className="text-xs font-mono whitespace-pre-wrap max-h-[55vh] overflow-y-auto p-3 border border-border rounded-sm bg-background">
                                    {textContent || '(empty)'}
                                </pre>
                            )}
                            {kind === 'other' && (
                                <div className="rounded-sm border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                                    Preview unavailable for this file type. Copy the URL to open externally.
                                </div>
                            )}
                            {meta && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-border text-xs">
                                    <div><div className="text-muted-foreground">Size</div><div>{fmtBytes(meta.size)}</div></div>
                                    <div><div className="text-muted-foreground">Type</div><div className="truncate">{meta.mime_type}</div></div>
                                    <div><div className="text-muted-foreground">Modified</div><div>{fmtDate(meta.modified)}</div></div>
                                    <div><div className="text-muted-foreground">Storage</div><div>{meta.storage_class}</div></div>
                                    <div className="col-span-2 md:col-span-4"><div className="text-muted-foreground">ETag</div><div className="truncate font-mono">{meta.etag || '—'}</div></div>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="p-4 border-t border-border flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex gap-2">
                        {url && (
                            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => copy(url, 'Signed URL')}>
                                <Copy size={12} className="mr-1.5" /> Signed URL
                            </Button>
                        )}
                        {publicUrl && (
                            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => copy(publicUrl, 'CDN URL')}>
                                <Copy size={12} className="mr-1.5" /> CDN URL
                            </Button>
                        )}
                    </div>
                    <Button
                        size="sm"
                        onClick={onClose}
                        data-testid={EXPLORER.closePreview}
                        className="rounded-sm"
                    >
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function SpaceExplorer() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [space, setSpace] = useState(null);
    const [prefix, setPrefix] = useState('');
    const [browse, setBrowse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [history, setHistory] = useState([]);
    // Admin-only cross-Space copy state
    const [selected, setSelected] = useState({}); // { key: true }
    const [copyOpen, setCopyOpen] = useState(false);
    const [allSpaces, setAllSpaces] = useState([]);
    const [destSpaceId, setDestSpaceId] = useState('');
    const [destPrefix, setDestPrefix] = useState('');
    const [copyStarting, setCopyStarting] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(null); // 'row-<key>' | 'bulk' | null
    const [deleting, setDeleting] = useState(false);
    const isAdmin = user?.role === 'admin';

    const loadSpace = useCallback(async () => {
        try {
            const { data } = await api.get('/spaces');
            const s = data.find((sp) => sp.id === id);
            if (!s) {
                toast.error('Space not found or not assigned to you');
                nav('/spaces');
                return;
            }
            setSpace(s);
        } catch (e) { toast.error(formatApiError(e)); }
    }, [id, nav]);

    const loadBrowse = useCallback(async (p) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/spaces/${id}/browse`, { params: { prefix: p } });
            setBrowse(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [id]);

    useEffect(() => { loadSpace(); }, [loadSpace]);
    useEffect(() => { loadBrowse(prefix); }, [prefix, loadBrowse]);

    const goTo = (p) => {
        setHistory((h) => [...h, prefix]);
        setPrefix(p);
        setSearchResults(null);
        setSelected({}); // clear selection on folder navigation
    };
    const goBack = () => {
        setHistory((h) => {
            if (h.length === 0) return h;
            const last = h[h.length - 1];
            setPrefix(last);
            setSearchResults(null);
            setSelected({});
            return h.slice(0, -1);
        });
    };

    const copyUrl = async (key) => {
        try {
            const { data } = await api.get(`/spaces/${id}/preview`, { params: { key } });
            await navigator.clipboard.writeText(data.url);
            toast.success('Signed URL copied (valid 5 minutes)');
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doSearch = async () => {
        setSearching(true);
        try {
            const { data } = await api.get(`/spaces/${id}/search`, { params: { q, folder: prefix } });
            setSearchResults(data.results);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSearching(false); }
    };

    // Selection helpers (admin only)
    const selectedKeys = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
    const toggleSelect = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
    const clearSelection = () => setSelected({});

    const openCopyDialog = async () => {
        try {
            const { data } = await api.get('/spaces');
            setAllSpaces(data.filter((s) => s.id !== id));
            setDestSpaceId('');
            setDestPrefix('');
            setCopyOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const startCopy = async () => {
        if (!destSpaceId || selectedKeys.length === 0) return;
        setCopyStarting(true);
        try {
            await api.post('/copy-jobs', {
                source_space_id: id,
                dest_space_id: destSpaceId,
                source_keys: selectedKeys,
                dest_prefix: destPrefix,
            });
            toast.success(`Started copying ${selectedKeys.length} file(s). See Jobs page for progress.`);
            setCopyOpen(false);
            setSelected({});
            nav('/jobs');
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCopyStarting(false); }
    };

    const performDelete = async (keys) => {
        setDeleting(true);
        try {
            const { data } = await api.post(`/spaces/${id}/delete-objects`, { keys });
            const failed = (data.failed || []).length;
            if (failed === 0) {
                toast.success(`Deleted ${data.deleted} file(s)`);
            } else {
                toast.warning(`Deleted ${data.deleted}, ${failed} failed`);
            }
            setDeleteOpen(null);
            setSelected((s) => {
                const next = { ...s };
                keys.forEach((k) => delete next[k]);
                return next;
            });
            loadBrowse(prefix);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };

    // Ctrl+K opens search
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setSearchOpen(true);
            } else if (e.key === 'Escape') {
                setSearchOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const stats = space?.stats || {};
    const filesToShow = useMemo(() => searchResults ?? browse?.files ?? [], [searchResults, browse]);

    if (!space) {
        return (
            <div className="space-y-3">
                <Skeleton className="h-8 w-64 rounded-sm" />
                <Skeleton className="h-4 w-96 rounded-sm" />
                <Skeleton className="h-64 w-full rounded-sm" />
            </div>
        );
    }

    return (
        <div data-testid={EXPLORER.root} className="space-y-6">
            {/* Header card */}
            <div className="rounded-sm border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-3 mt-1">
                            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight truncate">
                                {space.name}
                            </h1>
                            <span className="text-xs px-2 py-1 rounded-sm border border-border text-muted-foreground">
                                {space.region}
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 truncate">
                            {space.endpoint} · {space.bucket}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => nav(`/explorer/${id}/stats`)}
                            className="rounded-sm"
                        >
                            <BarChart3 size={13} className="mr-1.5" /> Statistics
                        </Button>
                        {user?.role === 'admin' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                    try {
                                        await api.post(`/spaces/${id}/refresh`);
                                        toast.success('Stats refreshed');
                                        loadSpace();
                                    } catch (e) { toast.error(formatApiError(e)); }
                                }}
                                className="rounded-sm"
                            >
                                <RefreshCw size={13} className="mr-1.5" /> Rescan
                            </Button>
                        )}
                    </div>
                </div>
                <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                        <div className="text-xs text-muted-foreground">Storage</div>
                        <div className="font-semibold text-sm mt-1 flex items-center gap-1.5"><HardDrive size={12} />{fmtBytes(stats.total_size || 0)}</div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground">Files</div>
                        <div className="font-semibold text-sm mt-1 flex items-center gap-1.5"><FileStack size={12} />{(stats.total_files || 0).toLocaleString()}</div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground">Folders</div>
                        <div className="font-semibold text-sm mt-1">{stats.total_folders || 0}</div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground">Last scan</div>
                        <div className="font-semibold text-xs mt-1">{fmtDate(space.last_scan)}</div>
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground">Status</div>
                        <div className="font-semibold text-sm mt-1">{space.status}</div>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={goBack}
                        disabled={history.length === 0 && !searchResults}
                        className="rounded-sm"
                    >
                        <ChevronLeft size={14} />
                    </Button>
                    {searchResults ? (
                        <div className="text-sm">
                            <span className="text-muted-foreground">Search results:</span>{' '}
                            <span className="text-foreground">&quot;{q}&quot;</span>{' '}
                            <button
                                onClick={() => { setSearchResults(null); setQ(''); }}
                                className="ml-2 text-primary hover:underline"
                            >
                                clear
                            </button>
                        </div>
                    ) : (
                        <Breadcrumb prefix={prefix} onNav={goTo} />
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            data-testid={EXPLORER.searchInput}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && q && doSearch()}
                            placeholder="Search files… (⌘K)"
                            className="rounded-sm pl-8 h-9 w-64 text-sm"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={doSearch}
                        disabled={!q || searching}
                        className="rounded-sm"
                    >
                        {searching ? <Loader2 className="animate-spin" size={14} /> : 'Search'}
                    </Button>
                </div>
            </div>

            {/* Admin selection toolbar */}
            {isAdmin && selectedKeys.length > 0 && (
                <div className="rounded-sm border border-primary/50 bg-primary/5 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
                    <div className="text-sm">
                        <span className="font-semibold">{selectedKeys.length}</span> file(s) selected
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={clearSelection}
                            data-testid={EXPLORER.clearSelection}
                            className="rounded-sm"
                        >
                            <XSquare size={13} className="mr-1.5" /> Clear
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteOpen('bulk')}
                            data-testid={EXPLORER.bulkDelete}
                            className="rounded-sm text-destructive border-destructive/40 hover:bg-destructive/10"
                        >
                            <Trash2 size={13} className="mr-1.5" /> Delete
                        </Button>
                        <Button
                            size="sm"
                            onClick={openCopyDialog}
                            data-testid={EXPLORER.copyToButton}
                            className="rounded-sm bg-primary hover:bg-primary/90"
                        >
                            <ArrowRightLeft size={13} className="mr-1.5" /> Copy to…
                        </Button>
                    </div>
                </div>
            )}

            {/* Body */}
            <div className="rounded-sm border border-border bg-card">
                {/* Unified table: folders first, then files */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm table-tech">
                        <thead>
                            <tr className="border-b border-border">
                                {isAdmin && (
                                    <th className="text-left px-3 py-3 w-10">
                                        <Checkbox
                                            data-testid={EXPLORER.selectAll}
                                            checked={filesToShow.length > 0 && filesToShow.every((f) => selected[f.key])}
                                            onCheckedChange={(v) => {
                                                if (v) {
                                                    const next = { ...selected };
                                                    filesToShow.forEach((f) => { next[f.key] = true; });
                                                    setSelected(next);
                                                } else {
                                                    const next = { ...selected };
                                                    filesToShow.forEach((f) => { delete next[f.key]; });
                                                    setSelected(next);
                                                }
                                            }}
                                            className="rounded-sm"
                                        />
                                    </th>
                                )}
                                <th className="text-left px-4 py-3">Name</th>
                                <th className="text-left px-4 py-3">Type</th>
                                <th className="text-left px-4 py-3">Size</th>
                                <th className="text-left px-4 py-3">Modified</th>
                                <th className="text-left px-4 py-3">Storage</th>
                                <th className="text-right px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={isAdmin ? 7 : 6} className="text-center p-8 text-muted-foreground"><Loader2 className="animate-spin inline" size={16} /></td></tr>
                            ) : (
                                <>
                                    {/* Folders as rows (hide during search) */}
                                    {!searchResults && browse && browse.folders.map((folder) => (
                                        <tr
                                            key={`fld-${folder.prefix}`}
                                            data-testid={EXPLORER.folder(folder.name)}
                                            onClick={() => goTo(folder.prefix)}
                                            className="border-b border-border row-hover hover:bg-muted/40 cursor-pointer"
                                        >
                                            {isAdmin && <td className="px-3 py-2.5"></td>}
                                            <td className="px-4 py-2.5 max-w-[420px]">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Folder size={16} className="text-primary shrink-0" />
                                                    <span className="truncate text-sm font-medium">{folder.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-muted-foreground">Folder</td>
                                            <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                            <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                            <td className="px-4 py-2.5 text-xs text-muted-foreground">—</td>
                                            <td className="px-4 py-2.5">
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={(e) => { e.stopPropagation(); goTo(folder.prefix); }}
                                                        className="h-7 w-7 rounded-sm"
                                                        aria-label="Open folder"
                                                    >
                                                        <ChevronRight size={14} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {/* Files (or search results) */}
                                    {filesToShow.length === 0 && (!browse || browse.folders.length === 0 || searchResults) ? (
                                        <tr><td colSpan={isAdmin ? 7 : 6} className="text-center p-12 text-muted-foreground">
                                            {searchResults ? 'No matches for your search.' : 'This folder is empty.'}
                                        </td></tr>
                                    ) : filesToShow.map((f, i) => {
                                        const ext = f.name.includes('.') ? f.name.split('.').pop().toLowerCase() : '';
                                        const isSel = !!selected[f.key];
                                        return (
                                            <tr
                                                key={f.key + i}
                                                data-testid={EXPLORER.fileRow(i)}
                                                className={`border-b border-border last:border-0 row-hover hover:bg-muted/40 ${isSel ? 'bg-primary/5' : ''}`}
                                            >
                                                {isAdmin && (
                                                    <td className="px-3 py-2.5">
                                                        <Checkbox
                                                            checked={isSel}
                                                            onCheckedChange={() => toggleSelect(f.key)}
                                                            data-testid={EXPLORER.selectRow(i)}
                                                            className="rounded-sm"
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-4 py-2.5 max-w-[420px]">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        {fileIcon(f.name)}
                                                        <span className="truncate text-sm">{f.name}</span>
                                                    </div>
                                                    {searchResults && (
                                                        <div className="text-xs text-muted-foreground truncate mt-0.5">{f.key}</div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-xs text-muted-foreground">{ext || '—'}</td>
                                                <td className="px-4 py-2.5 text-xs">{fmtBytes(f.size)}</td>
                                                <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDate(f.modified)}</td>
                                                <td className="px-4 py-2.5 text-xs text-muted-foreground">{f.storage_class || 'STANDARD'}</td>
                                                <td className="px-4 py-2.5">
                                                    <div className="flex justify-end gap-1">
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            onClick={() => setPreview(f.key)}
                                                            data-testid={EXPLORER.previewButton(i)}
                                                            className="h-7 w-7 rounded-sm"
                                                            aria-label="Preview"
                                                        >
                                                            <Eye size={13} />
                                                        </Button>
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            onClick={() => copyUrl(f.key)}
                                                            data-testid={EXPLORER.copyUrlButton(i)}
                                                            className="h-7 w-7 rounded-sm"
                                                            aria-label="Copy URL"
                                                        >
                                                            <Copy size={13} />
                                                        </Button>
                                                        {isAdmin && (
                                                            <Button
                                                                size="icon"
                                                                variant="ghost"
                                                                onClick={() => setDeleteOpen(`row-${f.key}`)}
                                                                data-testid={EXPLORER.deleteRow(i)}
                                                                className="h-7 w-7 rounded-sm text-destructive"
                                                                aria-label="Delete file"
                                                            >
                                                                <Trash2 size={13} />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
                {!searchResults && browse?.is_truncated && (
                    <div className="p-3 text-center text-xs text-muted-foreground border-t border-border">
                        Showing first {browse.files.length} items. Use search to narrow down.
                    </div>
                )}
            </div>

            <PreviewModal
                space={id}
                fileKey={preview}
                onClose={() => setPreview(null)}
                cdnUrl={space.cdn_url}
            />

            {/* Copy-to Space dialog (admin only) */}
            <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
                <DialogContent data-testid={EXPLORER.copyDialog} className="bg-popover border-border rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            Copy {selectedKeys.length} file(s) to another Space
                        </DialogTitle>
                        <DialogDescription>
                            Files are copied to the destination. The originals stay in place.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label className="text-sm">Destination Space</Label>
                            <Select value={destSpaceId} onValueChange={setDestSpaceId}>
                                <SelectTrigger data-testid={EXPLORER.copyDestSpace} className="rounded-sm">
                                    <SelectValue placeholder="Choose a Space" />
                                </SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                    {allSpaces.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            {s.name} · {s.region} · {s.bucket}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {allSpaces.length === 0 && (
                                <div className="text-xs text-muted-foreground">
                                    No other Spaces configured. Add another Space first.
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm">
                                Destination folder path <span className="text-muted-foreground">(optional)</span>
                            </Label>
                            <Input
                                value={destPrefix}
                                onChange={(e) => setDestPrefix(e.target.value)}
                                placeholder="e.g. archive/2026/"
                                data-testid={EXPLORER.copyDestPrefix}
                                className="rounded-sm"
                            />
                            <div className="text-xs text-muted-foreground">
                                Leave blank to copy to bucket root. File names are preserved.
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={startCopy}
                            disabled={copyStarting || !destSpaceId || selectedKeys.length === 0}
                            data-testid={EXPLORER.copyStart}
                            className="rounded-sm bg-primary hover:bg-primary/90"
                        >
                            {copyStarting ? <Loader2 className="animate-spin mr-2" size={14} /> : <ArrowRightLeft size={13} className="mr-2" />}
                            Start copy
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation (row + bulk) */}
            <AlertDialog open={!!deleteOpen} onOpenChange={(v) => !v && setDeleteOpen(null)}>
                <AlertDialogContent className="rounded-sm border-border">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {deleteOpen === 'bulk'
                                ? `Delete ${selectedKeys.length} file(s)?`
                                : 'Delete this file?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteOpen === 'bulk' ? (
                                <>The selected files will be permanently removed from the bucket. This cannot be undone.</>
                            ) : (
                                <>
                                    <span className="font-mono break-all">{deleteOpen?.startsWith('row-') ? deleteOpen.slice(4) : ''}</span>
                                    <br />This file will be permanently removed from the bucket. This cannot be undone.
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm" disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                if (deleteOpen === 'bulk') {
                                    performDelete(selectedKeys);
                                } else if (deleteOpen?.startsWith('row-')) {
                                    performDelete([deleteOpen.slice(4)]);
                                }
                            }}
                            disabled={deleting}
                            data-testid={EXPLORER.deleteConfirm}
                            className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleting && <Loader2 className="animate-spin mr-2" size={13} />}
                            Delete permanently
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Command-K quick search */}
            <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
                <DialogContent className="bg-popover border-border rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-display">Quick search</DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center gap-2">
                        <Input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && q && (doSearch(), setSearchOpen(false))}
                            placeholder="Type filename fragment…"
                            className="rounded-sm h-11"
                        />
                        <Button
                            onClick={() => { doSearch(); setSearchOpen(false); }}
                            disabled={!q}
                            className="rounded-sm h-11"
                        >
                            Search
                        </Button>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Searches inside the current Space. Filter by name; use the search input above for full options.
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
